/**
 * Wallet's end of the capture port.
 *
 * The extension stays the authority for everything: the catalog, the pairings,
 * the ordering, the auth and the run state. This file holds a connection open,
 * asks three questions on the way up, relays two commands, and renders whatever
 * it is told. It contains NO registry knowledge -- no hostnames, no slug lists,
 * no idea which issuer pairs with which program -- because a second copy of any
 * of that is a button that disagrees with the run behind it.
 *
 * A long-lived port and not one message per command, for two reasons. A run
 * outlives any single request, so the page has to be TOLD about progress rather
 * than poll for it; and the port's `onDisconnect` is the only honest signal that
 * Chrome shut the service worker down, which is the thing a reconnect exists to
 * survive.
 *
 * No chrome types: the transport is injected, which is also what makes every
 * state below testable without a browser.
 */

import {
  PROTOCOL_VERSION,
  isReply, isSnapshotEvent,
  type Catalog, type FailureCode, type HelloResult, type Snapshot, type Verb,
} from "../../../shared/capturePort";

/** The part of a chrome Port this file uses. See chromeCapture.ts for the real one. */
export interface PortTransport {
  postMessage(message: unknown): void;
  disconnect(): void;
  onMessage(fn: (message: unknown) => void): void;
  onDisconnect(fn: () => void): void;
}

/**
 * How the connection stands. Four of these five are reachable on a desktop with
 * the extension loaded; `absent` is every other browser, including every phone.
 *
 *   connecting    first handshake in flight; draw nothing yet
 *   ready         handshake done, catalog in hand
 *   reconnecting  was ready, the worker went away; last known state still shown
 *   absent        no extension API, or the handshake never answered
 *   outdated      something answered, and it does not speak this protocol
 *
 * `absent` and `outdated` are deliberately separate. Absent means there is
 * nothing to say and nothing to press, so Wallet draws no control at all.
 * Outdated is actionable and otherwise invisible: a silent redirect would read
 * as "the extension is gone" when it is loaded and needs rebuilding.
 */
export type Connection = "connecting" | "ready" | "reconnecting" | "absent" | "outdated";

export interface BridgeState {
  connection: Connection;
  /** Null until a handshake has succeeded once. */
  hello: HelloResult | null;
  catalog: Catalog | null;
  snapshot: Snapshot | null;
  /** The protocol the other side speaks, when it told us and it was not ours. */
  speaks: number | null;
  /**
   * The handshake worked and the catalog did not load.
   *
   * Distinct from an EMPTY catalog, which is a registry serving nothing, and from
   * a null one, which is "not asked yet". Without the distinction the page drew
   * Run all over no rows and read as "there is nothing to capture".
   */
  catalogFailed: boolean;
}

export interface CaptureBridge {
  state: BridgeState;
  subscribe(fn: (state: BridgeState) => void): () => void;
  /** `undefined` is Run all: no filter at all, never an empty list. */
  start(slugs?: string[]): Promise<{ started: true } | { error: FailureCode }>;
  stop(): Promise<void>;
  /** Reload the extension; the port drops and reconnects to the new build. */
  reload(): Promise<{ reloading: true } | { error: FailureCode }>;
  /** Re-ask `hello` on a working connection, to see a build made since. */
  recheck(): void;
  /** Ask again after the operator says they have fixed something. */
  retry(): void;
  close(): void;
}

export interface BridgeDeps {
  /**
   * Open a port, or null when this browser has no extension API at all.
   *
   * Null is the common case and not an error: it is every phone, every other
   * browser, and every Chrome profile without the extension loaded. A connector
   * that THROWS is treated the same way -- chrome raises synchronously for some
   * malformed ids.
   */
  connect: () => PortTransport | null;
  /**
   * Called once each time a run finishes, so the pages that show what a run
   * writes can refetch. Not called for a run that was already finished when the
   * page loaded, which would refetch on every navigation to no purpose.
   */
  onRunFinished?: () => void;
}

/**
 * How long to wait for `hello` before calling it absent.
 *
 * Chrome usually answers a missing extension by disconnecting at once, which is
 * faster than this and is the path that normally decides. The timer covers the
 * other shape: a port that opens and never answers, which is what a worker
 * wedged on startup looks like.
 */
export const HANDSHAKE_TIMEOUT_MS = 4000;

/**
 * Backoff between reconnects, in milliseconds, then the last value forever.
 *
 * The first retry is quick because the ordinary cause is Chrome shutting an idle
 * service worker down, and connecting is what wakes it: that reconnect succeeds
 * immediately. The tail is slow because the other cause is an extension that has
 * been unloaded, and there is no point hammering it.
 */
export const RECONNECT_BACKOFF_MS = [500, 2000, 10_000] as const;

/**
 * How long any one request may go unanswered.
 *
 * Generous, because the slow case is the extension's FIRST token mint of a worker
 * lifetime and it is followed by a round trip to Wallet's API. Bounded, because
 * the alternative is what shipped: a page waiting for ever on a request nobody
 * was going to answer.
 */
export const REQUEST_TIMEOUT_MS = 20_000;

/**
 * The deadline for a verb whose own work is a storage read.
 *
 * `start` and `stop` do not mint a token or call Wallet's API: `start` answers on
 * acceptance and `stop` raises a flag and closes tabs. Sharing the generous
 * deadline above left the button spinning for twenty seconds against a wedged
 * worker, which is the one moment the operator is already pressing it twice.
 */
export const COMMAND_TIMEOUT_MS = 5_000;

const INITIAL: BridgeState = {
  connection: "connecting", hello: null, catalog: null, snapshot: null, speaks: null,
  catalogFailed: false,
};

export function createBridge(deps: BridgeDeps): CaptureBridge {
  let state: BridgeState = INITIAL;
  const watchers = new Set<(state: BridgeState) => void>();
  let port: PortTransport | null = null;
  let closed = false;
  let attempt = 0;
  /**
   * Which connection attempt is current.
   *
   * Load-bearing, and the bug it fixes was subtle. When a port drops,
   * `settleAll` resolves every request in flight as failed -- including the
   * `hello` that `handshake` is awaiting. `handshake` then carries on and treats
   * that as a failed handshake, so BOTH it and the disconnect handler schedule a
   * reconnect: two ports race, each dropping and scheduling again, and the
   * backoff means nothing. An awaited answer is only acted on while the attempt
   * that asked for it is still the current one.
   *
   * It therefore has to advance when an attempt DIES, not only when one starts:
   * bumping it on open alone leaves the dead port's resumed handshake looking
   * current, which is the whole of the bug.
   */
  let epoch = 0;

  /** This attempt is over; nothing it asked for may be acted on any more. */
  function invalidate() {
    epoch += 1;
  }
  let handshakeTimer: ReturnType<typeof setTimeout> | undefined;
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

  // Request id to its resolver. A port carries replies for every request in
  // flight, so each one has to find its own; a single pending slot would hand
  // `catalog`'s answer to whoever asked last.
  let nextId = 0;
  const pending = new Map<string, (reply: { ok: true; result: unknown } | { ok: false; code: FailureCode }) => void>();

  const bridge: CaptureBridge = {
    get state() { return state; },
    subscribe(fn) { watchers.add(fn); return () => { watchers.delete(fn); }; },
    start,
    stop,
    reload,
    recheck,
    retry,
    close,
  };

  function publish(next: Partial<BridgeState>) {
    state = { ...state, ...next };
    for (const fn of watchers) fn(state);
  }

  /**
   * Send a request and wait for its reply.
   *
   * Rejects when the port is gone rather than hanging: a command the operator
   * pressed has to come back one way or the other, and "the worker died while
   * you were pressing Run" is a thing to say rather than a spinner forever.
   */
  function ask(verb: Verb, extra: Record<string, unknown> = {},
               deadlineMs = REQUEST_TIMEOUT_MS): Promise<
    { ok: true; result: unknown } | { ok: false; code: FailureCode }
  > {
    const live = port;
    if (!live) return Promise.resolve({ ok: false as const, code: "failed" as FailureCode });
    const id = `w${(nextId += 1)}`;
    return new Promise((resolve) => {
      // A deadline, because the other side can be alive and not answering: the
      // worker's first token mint is the slow case, and before this a hung one
      // left the page on "Looking for the capture extension…" indefinitely with
      // nothing to press. Every answer is now bounded, so a caller always gets
      // one. (2026-10-04)
      const timer = setTimeout(() => {
        if (pending.delete(id)) resolve({ ok: false, code: "failed" });
      }, deadlineMs);
      pending.set(id, (answer) => {
        clearTimeout(timer);
        resolve(answer);
      });
      live.postMessage({ v: PROTOCOL_VERSION, id, verb, ...extra });
    });
  }

  function settleAll(code: FailureCode) {
    for (const resolve of pending.values()) resolve({ ok: false, code });
    pending.clear();
  }

  function onMessage(message: unknown) {
    // A pushed snapshot: the run moved, and nothing here asked.
    if (isSnapshotEvent(message)) {
      noteFinished(message.snapshot);
      publish({ snapshot: message.snapshot });
      return;
    }
    if (!isReply(message)) return;
    const resolve = pending.get(message.id);
    if (!resolve) return;
    pending.delete(message.id);
    resolve(message.ok
      ? { ok: true, result: message.result }
      : { ok: false, code: message.code });
  }

  /**
   * Fire `onRunFinished` on the live -> not live edge only.
   *
   * The edge and not the state: a page that loaded after a run finished would
   * otherwise refetch on arrival forever, and a snapshot pushed every ten
   * seconds by the heartbeat would refetch ten times a minute.
   */
  function noteFinished(next: Snapshot) {
    if (state.snapshot?.live && !next.live) deps.onRunFinished?.();
  }

  function clearHandshakeTimer() {
    if (handshakeTimer !== undefined) clearTimeout(handshakeTimer);
    handshakeTimer = undefined;
  }

  /**
   * Ask the three questions that make a connection usable.
   *
   * In this order, and `hello` alone first: its answer decides whether the other
   * two are worth asking at all. An outdated worker would refuse them with the
   * same `protocol_mismatch`, and three identical refusals is a worse way to
   * learn one fact.
   */
  async function handshake(mine: number) {
    const hello = await ask("hello");
    if (closed || epoch !== mine) return;
    clearHandshakeTimer();
    if (!hello.ok) {
      // The one refusal the operator can act on. `speaks` is unknown here --
      // the other side answers in ITS version, and all we know is that it is not
      // ours -- so the page says which version IT needs.
      if (hello.code === "protocol_mismatch") {
        teardown();
        publish({ connection: "outdated", speaks: null });
        return;
      }
      fallBack();
      return;
    }
    const result = hello.result as HelloResult;
    // Belt and braces: a worker that answered `hello` while speaking another
    // protocol version is a build mismatch our own parse would not have caught,
    // because `hello` carries the number as data as well.
    if (result.protocol !== PROTOCOL_VERSION) {
      teardown();
      publish({ connection: "outdated", speaks: result.protocol });
      return;
    }
    // Published the MOMENT the handshake is good, before the catalog is asked.
    //
    // Two bugs lived in waiting for the catalog first. The page showed nothing
    // until a round trip to Wallet's API had completed, which on a cold worker's
    // first token mint is seconds. And `hello` stayed unrecorded, so a port that
    // dropped during the catalog fetch looked like a connection that never
    // happened -- `absent`, which /refresh redirects on -- instead of a worker
    // that went away, which reconnects. That is exactly how a cold load ended on
    // Points ~18s after asking for /refresh. (2026-10-04)
    attempt = 0;
    publish({ connection: "ready", hello: result, speaks: PROTOCOL_VERSION,
              catalogFailed: false });
    await loadCatalog(mine);
  }

  /**
   * Fetch the catalog and the current run state, once the handshake is good.
   *
   * A failure here is reported rather than fatal: the connection is fine and the
   * rules come from Wallet's API, so this is what a cold start or a 5xx looks
   * like. The last catalog stays on screen -- it was right a moment ago -- and
   * `catalogFailed` is what lets the page say so instead of drawing an empty list
   * that reads as "nothing to capture".
   */
  async function loadCatalog(mine: number) {
    const [catalog, snapshot] = await Promise.all([ask("catalog"), ask("snapshot")]);
    if (closed || epoch !== mine) return;
    publish({
      catalog: catalog.ok ? (catalog.result as Catalog) : state.catalog,
      snapshot: snapshot.ok ? (snapshot.result as Snapshot) : state.snapshot,
      catalogFailed: !catalog.ok,
    });
  }

  /** Give up on this attempt: absent the first time, reconnecting after that. */
  function fallBack() {
    teardown();
    if (state.hello) {
      publish({ connection: "reconnecting" });
      scheduleReconnect();
      return;
    }
    publish({ connection: "absent" });
  }

  function scheduleReconnect() {
    if (closed) return;
    const wait = RECONNECT_BACKOFF_MS[Math.min(attempt, RECONNECT_BACKOFF_MS.length - 1)]!;
    attempt += 1;
    reconnectTimer = setTimeout(() => { reconnectTimer = undefined; open(); }, wait);
  }

  function teardown() {
    invalidate();
    clearHandshakeTimer();
    settleAll("failed");
    const live = port;
    port = null;
    // Already gone when the disconnect came from the other side.
    try { live?.disconnect(); } catch { /* nothing to close */ }
  }

  function open() {
    if (closed) return;
    epoch += 1;
    const mine = epoch;
    let transport: PortTransport | null = null;
    try {
      transport = deps.connect();
    } catch {
      // Chrome raises synchronously for some malformed ids; same outcome as a
      // browser with no extension API at all.
      transport = null;
    }
    if (!transport) {
      // A connect that cannot even open a port, on a RECONNECT, must still come
      // back: this branch published "reconnecting" and returned, so one failed
      // attempt stranded the bridge for the life of the page -- no timer, no
      // further connect, and no retry control, since the page offers one only for
      // `outdated` and a failed catalog. Chrome invalidates an open tab's
      // extension context every time the extension is reloaded, which is most of
      // a development session, so this was the common case rather than an edge.
      // `fallBack` is not reused here because there is no port to tear down.
      // (2026-10-04)
      if (state.hello) {
        publish({ connection: "reconnecting" });
        scheduleReconnect();
        return;
      }
      publish({ connection: "absent" });
      return;
    }
    port = transport;
    transport.onMessage(onMessage);
    transport.onDisconnect(() => {
      if (port !== transport) return;
      port = null;
      invalidate();
      settleAll("failed");
      // Before the branch, not inside one. The timer belongs to the port that
      // just died, and left running it fires later and schedules a SECOND
      // reconnect on top of the one below -- two ports racing, each dropping
      // and scheduling again. Caught by the backoff test, which saw one more
      // connection than it had advanced the clock for.
      clearHandshakeTimer();
      if (closed) return;
      // Was ready, so the extension exists and the worker was shut down: keep
      // what we know on screen and reconnect. Never ready, so there is probably
      // nothing to connect to.
      if (state.hello) {
        publish({ connection: "reconnecting" });
        scheduleReconnect();
      } else {
        publish({ connection: "absent" });
      }
    });
    handshakeTimer = setTimeout(() => {
      handshakeTimer = undefined;
      if (epoch === mine) fallBack();
    }, HANDSHAKE_TIMEOUT_MS);
    void handshake(mine);
  }

  async function start(slugs?: string[]): Promise<{ started: true } | { error: FailureCode }> {
    // No filter at all for Run all. An empty list would reach the extension's
    // membership filter and run nothing while reporting a started run.
    const answer = await ask("start", slugs === undefined ? {} : { slugs },
                             COMMAND_TIMEOUT_MS);
    if (!answer.ok) return { error: answer.code };
    // Ask once rather than wait for the push: the first snapshot event may be a
    // few hundred milliseconds out and the button has to stop looking pressable.
    void refreshSnapshot();
    return { started: true };
  }

  async function stop() {
    await ask("stop", {}, COMMAND_TIMEOUT_MS);
    void refreshSnapshot();
  }

  async function reload(): Promise<{ reloading: true } | { error: FailureCode }> {
    const answer = await ask("reload", {}, COMMAND_TIMEOUT_MS);
    return answer.ok ? { reloading: true } : { error: answer.code };
  }

  async function recheck() {
    if (closed || !port || state.connection !== "ready") return;
    const mine = epoch;
    const answer = await ask("hello");
    if (closed || epoch !== mine || !answer.ok) return;
    const result = answer.result as HelloResult;
    if (result.protocol === PROTOCOL_VERSION) publish({ hello: result });
  }

  async function refreshSnapshot() {
    const answer = await ask("snapshot");
    if (answer.ok && !closed) {
      const next = answer.result as Snapshot;
      noteFinished(next);
      publish({ snapshot: next });
    }
  }

  function retry() {
    if (closed) return;
    // The port is fine and only the catalog failed: re-ask for it rather than
    // tearing a working connection down. This is the common case behind the
    // page's Retry, since the usual cause is Wallet's API cold-starting.
    if (port && state.connection === "ready") {
      publish({ catalogFailed: false });
      void loadCatalog(epoch);
      return;
    }
    attempt = 0;
    if (reconnectTimer !== undefined) {
      clearTimeout(reconnectTimer);
      reconnectTimer = undefined;
    }
    teardown();
    publish({ connection: state.hello ? "reconnecting" : "connecting", speaks: null });
    open();
  }

  function close() {
    closed = true;
    if (reconnectTimer !== undefined) clearTimeout(reconnectTimer);
    reconnectTimer = undefined;
    teardown();
  }

  open();
  return bridge;
}
